import {
  BehaviorSubject,
  combineLatest,
  interval,
  Observable,
  switchMap,
} from 'rxjs';
import { map, startWith, throttleTime } from 'rxjs/operators';
import { CurrentShow } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { showInfo } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow.utils';
import type { CouponApi, MyCouponDto } from '../../generated/api';
import { StatusCode } from '../../models/ApiErrors';
import { CouponType } from '../../models/ClientModels';
import { memorizeWhileInFlight } from '../../utils/promiseHelpers';
import { assertNever } from '../../utils/typeHelper';
import { isNotNull, sortBy } from '../../utils/utils';
import { apiServerTimeToDateObject } from '../api-model-utils/apiServerTimeToDateObject';
import { skipForUnauthenticatedUsers } from '../auth/utils';
import { DisplayableError } from '../DisplayableError';
import { WithID } from '../firebase/rxfireExtensions';
import {
  UniversalCoupon,
  UniversalRedeemableCoupon,
  UniversalRedeemedCoupon,
} from '../models/coupon';
import { APIErrorWrapper } from '../services';
import { couponItemToUniversalCoupon } from './couponItemToUniversalCoupon';
import { availableCouponDtoToUniversalRedeemableCoupon } from './_/availableCouponDtoToUniversalRedeemableCoupon';
import { couponDetailDtoToUniversalRedeemableCoupon } from './_/couponDetailDtoToUniversalRedeemableCoupon';
import { relatedCouponsMayChanged$ } from './_/relatedCouponsMayChanged';

export class CouponServiceHelper {
  constructor(private couponService: CouponApi) {}
  manualRefresh$ = new BehaviorSubject(1);

  myCoupons$: Observable<WithID<UniversalRedeemedCoupon>[]> =
    skipForUnauthenticatedUsers(currentUserId => {
      const autoRefreshSignal$ = interval(1000 * 60 * 5).pipe(startWith(null));

      return combineLatest([autoRefreshSignal$, this.manualRefresh$]).pipe(
        throttleTime(1000, undefined, { leading: true }),
        switchMap(() =>
          /**
           * API is different with firestore, `/redeemedCoupons/:id` will not
           * update the `redeemedQuantity` once user redeemed the coupon, the
           * API will
           */
          this.couponService
            .couponApiControllerGetMyCoupons({ userId: currentUserId })
            .then(res =>
              res.coupons
                .map(myCouponDtoToUniversalRedeemedCoupon)
                .filter(isNotNull),
            ),
        ),
      );
    }, []).pipe(
      map(
        sortBy(coupon => {
          // used in the last
          if (coupon.usedAt) {
            return 'b' + coupon.coupon.createdAt.getTime();
          }

          return getCouponStandardAscOrderKey(coupon.coupon);
        }),
      ),
    );

  private requestGetAvailableCoupons = memorizeWhileInFlight(
    (sellerId?: string) =>
      this.couponService
        .couponApiControllerGetAvailableCoupons({ sellerId })
        .then(resp =>
          resp.coupons
            .map(availableCouponDtoToUniversalRedeemableCoupon)
            .filter(isNotNull),
        ),
  );

  getShowRelatedCoupons$(
    sellerId?: string,
  ): Observable<UniversalRedeemableCoupon[]> {
    return skipForUnauthenticatedUsers(
      () =>
        relatedCouponsMayChanged$.pipe(
          startWith(null),
          switchMap(() => this.requestGetAvailableCoupons(sellerId)),
        ),
      [],
    ).pipe(
      map(
        sortBy(coupon => {
          // redeemed in the last
          if (coupon.status === UniversalRedeemableCoupon.Status.Redeemed) {
            return 'bb' + coupon.coupon.createdAt.getTime();
          }

          // unmatched in the middle
          if (
            coupon.status === UniversalRedeemableCoupon.Status.Nonredeemable
          ) {
            return 'b' + coupon.coupon.createdAt.getTime();
          }

          // rest in the first
          return getCouponStandardAscOrderKey(coupon.coupon);
        }),
      ),
    );
  }

  private requestGetAvailableCouponDetail = memorizeWhileInFlight(
    (couponId: string, sellerId?: string) =>
      this.couponService
        .couponApiControllerGetCouponDetail({
          couponId,
          sellerId,
        })
        .then(res => {
          if (res.data == null) return null;
          return couponDetailDtoToUniversalRedeemableCoupon(res.data);
        }),
  );

  getShowRelatedCouponById$(
    couponId: string,
    show: CurrentShow,
  ): Observable<null | UniversalRedeemableCoupon> {
    return skipForUnauthenticatedUsers(
      () =>
        relatedCouponsMayChanged$.pipe(
          startWith(null),
          switchMap(() =>
            this.requestGetAvailableCouponDetail(
              couponId,
              showInfo(show).seller?.id,
            ),
          ),
        ),
      null,
    );
  }

  async redeemCode(
    code: string,
    context: {
      inShow?: CurrentShow;
    } = {},
  ): Promise<null | WithID<UniversalRedeemedCoupon>> {
    const sellerId = context.inShow
      ? showInfo(context.inShow).seller?.id
      : undefined;

    try {
      const resp = await this.couponService.couponApiControllerRedeemCoupon({
        code: code.toUpperCase(),
        redeemCouponRequest: { sellerId },
      });
      return myCouponDtoToUniversalRedeemedCoupon(resp);
    } catch (err) {
      if (
        sellerId == null &&
        err instanceof APIErrorWrapper &&
        err.status === StatusCode.INVALID_ARGUMENT &&
        err.details?.find(
          d =>
            d['@type'] === 'type.googleapis.com/google.rpc.BadRequest' &&
            d.fieldViolations.find(f => f.field === 'sellerId'),
        )
      ) {
        throw new DisplayableError(
          'Can not claim this promo code out of a show from the valid sellers',
          { cause: err },
        );
      }
      throw err;
    }
  }

  notifyMyCouponsMayChanged() {
    this.manualRefresh$.next(this.manualRefresh$.value + 1);
  }
}

export function myCouponDtoToUniversalRedeemedCoupon(
  coupon: MyCouponDto,
): null | WithID<UniversalRedeemedCoupon> {
  const universalCoupon = couponItemToUniversalCoupon(coupon.coupon);

  if (!universalCoupon) return null;

  return {
    id: coupon.redeemedCouponId,
    couponId: coupon.couponId,
    coupon: {
      ...universalCoupon,
      redeemedQuantity: coupon.redeemedQuantity,
    },
    usedAt: coupon.usedAt
      ? apiServerTimeToDateObject(coupon.usedAt)
      : undefined,
  };
}

function getCouponStandardAscOrderKey(coupon: UniversalCoupon): string {
  // expired in the last
  if (UniversalCoupon.isExpired(coupon)) {
    return 'aaa' + coupon.createdAt.getTime();
  }

  // free shipping in the first
  if (coupon.type === CouponType.freeShipping) {
    return '' + coupon.createdAt.getTime();
  }

  // discount amount coupon in the middle and order by discount amount
  if (coupon.type === CouponType.discountAmount) {
    return 'a' + (1000000 - coupon.discountAmount);
  }

  if (coupon.type === CouponType.discountPercent) {
    return 'aa' + (1000000 - coupon.discountPercent);
  }

  assertNever(coupon);
}
